import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

/**
 * Form field wrapper (§5.2): label ALWAYS above the field (never placeholder-as-label),
 * required mark in lime, error below wired via aria-describedby, help text muted.
 */
interface FieldShellProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string | null;
  help?: string;
  suffix?: string;
  children: ReactNode;
}

function describedBy(id: string, error?: string | null, help?: string): string | undefined {
  const ids = [error ? `${id}-error` : null, help ? `${id}-help` : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

function FieldShell({ id, label, required, error, help, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="type-label text-secondary">
        {label}
        {required ? <span className="text-accent"> *</span> : null}
      </label>
      {children}
      {help && !error ? (
        <p id={`${id}-help`} className="type-body-sm text-muted">
          {help}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="type-body-sm flex items-center gap-1.5 text-danger" role="alert">
          <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
            <path d="M7 1.5 13 12H1L7 1.5Z" strokeLinejoin="round" />
            <path d="M7 5.5v3M7 10.5v.5" strokeLinecap="round" />
          </svg>
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL =
  'h-11 w-full rounded-sm border bg-surface-2 px-3 font-mono text-[14px] text-primary placeholder:text-muted ' +
  'transition-colors duration-fast ease-machine focus-ring focus:border-accent';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | null;
  help?: string;
  suffix?: string;
}

export function TextField({ label, error, help, required, suffix, className = '', ...rest }: TextFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} required={required} error={error} help={help}>
      <div className="relative">
        <input
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, help)}
          className={[CONTROL, error ? 'border-danger' : 'border-border-interactive', suffix ? 'pr-14 text-right' : '', className].join(' ')}
          {...rest}
        />
        {suffix ? (
          <span className="type-label text-muted pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">{suffix}</span>
        ) : null}
      </div>
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  error?: string | null;
  help?: string;
}

export function TextAreaField({ label, error, help, required, className = '', rows = 4, ...rest }: TextAreaFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} required={required} error={error} help={help}>
      <textarea
        id={id}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, help)}
        className={[
          'w-full rounded-sm border bg-surface-2 px-3 py-2.5 font-body text-[15px] text-primary placeholder:text-muted',
          'transition-colors duration-fast ease-machine focus-ring focus:border-accent',
          error ? 'border-danger' : 'border-border-interactive',
          className,
        ].join(' ')}
        {...rest}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  error?: string | null;
  help?: string;
  children: ReactNode;
}

export function SelectField({ label, error, help, required, className = '', children, ...rest }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} required={required} error={error} help={help}>
      <select
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, help)}
        className={[CONTROL, error ? 'border-danger' : 'border-border-interactive', 'appearance-none', className].join(' ')}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
}
